// Explore's analysis: a chat beside the list about the traces the search
// reaches. Opened, it writes about the scope (lib/exploreAnalysis.js) while the
// space its answer will fill shows it thinking (ThinkingField). The traces it
// names are pinned to the top of the list, the names it writes filter for or
// out, and a filter it suggests applies as a turn of the chat, after which it
// writes about the new scope. A question asked below is answered about the
// same scope.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import Markdown from "../Markdown.jsx";
import ThinkingField from "../ThinkingField.jsx";
import { resolveTraceRefs, splitAnalysis, startExploreAnalysis, traceRefs } from "../../lib/exploreAnalysis.js";
import { reduceChatEvent } from "../../lib/leanChatTurns.js";

let nextId = 0;
const turnId = () => `t${++nextId}`;
const NUM = new Intl.NumberFormat("en-US");
// Space kept above a turn scrolled to the top, clear of the close button.
const TURN_TOP = 44;
const SHARE_FILL = [
  "color-mix(in srgb, var(--ink) 80%, transparent)",
  "color-mix(in srgb, var(--ink) 50%, transparent)",
  "color-mix(in srgb, var(--ink) 28%, transparent)",
  "var(--line-strong)",
];
const pct = (share) => `${Math.round((Number(share) || 0) * 100)}%`;
// An analysis links the names and traces it writes about; a note does not.
const LINKED = /\]\((?:trace|filter):/;
// What a follow-up said before a query, as a plain line for the thinking
// field: links to their words, no markdown marks, dashes as commas.
const noteLine = (note) => String(note || "")
  .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
  .replace(/[*`]/g, "")
  .replace(/^[#>\s]+/, "")
  .replace(/\s*[–—]\s*/g, ", ")
  .replace(/\s+/g, " ")
  .trim();
const negate = (clause) => clause.split(/\s+(?=-?[a-z_]+:)/i).map((c) => (c.startsWith("-") ? c.slice(1) : `-${c}`)).join(" ");

// The scope split by its largest parts: a bar of shares and its rows.
function Breakdown({ value, renderLever }) {
  const parts = [...(value.rows || []).slice(0, 3), ...(value.rest ? [value.rest] : [])];
  return (
    <div className="mb-[16px] rounded-[8px] border border-[color:var(--line)] px-[13px] pb-[7px] pt-[11px]" data-explore-breakdown="">
      <div className="mb-[9px] flex justify-between text-[12px] text-[color:var(--muted)]">
        <span>{value.title}</span>
        <span className="font-medium tabular-nums text-[color:var(--ink)]">{/^\d+$/.test(String(value.total)) ? NUM.format(Number(value.total)) : value.total}</span>
      </div>
      <div className="mb-[9px] flex h-[8px] gap-[2px]" aria-hidden="true">
        {parts.map((p, i) => <span key={i} className="min-w-[3px] rounded-[2px]" style={{ flex: Math.max(0.001, Number(p.share) || 0), background: SHARE_FILL[Math.min(i, 3)] }} />)}
      </div>
      {parts.map((p, i) => (
        <div key={i} className="grid h-[23px] grid-cols-[8px_minmax(0,1fr)_auto_34px] items-center gap-[9px] text-[13px]">
          <i aria-hidden="true" className="h-[8px] w-[8px] rounded-[2px]" style={{ background: SHARE_FILL[i === parts.length - 1 && value.rest ? 3 : Math.min(i, 2)] }} />
          <span className={`min-w-0 truncate ${p === value.rest ? "text-[color:var(--muted)]" : ""}`}>{p.filter ? renderLever(p.filter, p.label) : p.label}</span>
          <span className="text-right tabular-nums">{p.value}</span>
          <span className="text-right text-[12px] tabular-nums text-[color:var(--muted)]">{pct(p.share)}</span>
        </div>
      ))}
    </div>
  );
}

function Answer({ turn, status, minHeight, onLeft, fieldGone, renderLink, renderLever, onSuggest, onRetry }) {
  const { prose, breakdown, filters } = splitAnalysis(turn.text);
  const cut = prose.indexOf("\n\n");
  const lead = turn.question ? "" : cut < 0 ? prose : prose.slice(0, cut);
  const rest = turn.question ? prose : cut < 0 ? "" : prose.slice(cut + 2);
  const thinking = turn.status === "thinking";
  return (
    <div
      data-explore-answer={turn.id}
      aria-busy={turn.status === "thinking" || turn.status === "writing" ? "true" : undefined}
      className="relative -mx-[24px] px-[24px] pt-[24px]"
      style={thinking || !fieldGone ? { minHeight } : undefined}
    >
      {!fieldGone ? <ThinkingField status={status} leaving={!thinking} onLeft={onLeft} /> : null}
      {lead ? <div className="mb-[12px] text-[19px] leading-[27px] tracking-[-0.012em] text-[color:var(--ink)]"><Markdown text={lead} renderLink={renderLink} /></div> : null}
      {breakdown ? <Breakdown value={breakdown} renderLever={renderLever} /> : null}
      {rest ? <div className="text-[14px] leading-[21px] text-[color:var(--muted)] [&_p]:mb-[11px] [&_strong]:font-medium [&_strong]:text-[color:var(--ink)]"><Markdown text={rest} renderLink={renderLink} /></div> : null}
      {turn.status === "error" ? (
        <p className="m-0 text-[13px] text-[color:var(--muted)]">
          {turn.unread ? "The traces could not be read. Reload to try again." : (
            <>The analysis could not be written. <button type="button" onClick={onRetry} className="text-[color:var(--ink)] underline">Try again</button></>
          )}
        </p>
      ) : null}
      {turn.status === "done" && filters.length ? (
        <div className="mb-[4px] mt-[6px] grid gap-[6px]">
          {filters.map((f) => (
            <button
              key={f.clause}
              type="button"
              onClick={() => onSuggest(f)}
              className="flex min-h-[36px] items-center justify-between gap-[14px] rounded-[7px] border border-[color:var(--line-strong)] px-[12px] py-[7px] text-left text-[13px] leading-[18px] text-[color:var(--ink)] hover:border-[color:var(--green)]"
            >
              {f.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function AnalysisPanel({ facts, scopeKey, scopePath = [], status, failed = false, onClose, onPins, onFilter, onOpenTrace, onLightTrace }) {
  const [turns, setTurns] = useState([]);
  const [fieldGone, setFieldGone] = useState(() => new Set());
  const [heights, setHeights] = useState({});
  const [menu, setMenu] = useState(null);
  const [question, setQuestion] = useState("");
  const log = useRef(null);
  const runs = useRef(new Map());
  const analyzed = useRef(null);

  const patch = useCallback((id, next) => setTurns((all) => all.map((t) => (t.id === id ? { ...t, ...next } : t))), []);

  // Write one answer: the analysis of the scope, or the answer to a question.
  // Some models write a one-line note ("Composing the analysis...") before the
  // analysis or in place of it, so an analysis stays thinking until it links a
  // name or trace, its text is the run's result where there is one, and one
  // that ends without a link is asked again once. A run that fails (a model
  // may stop on a scope's security content) is written once more on the
  // second analysis model. The traces it links by the facts' refs are linked
  // by their ids. A question's answer shows once its run ends; while it
  // queries, what it last said before a query is the thinking field's status.
  const write = useCallback((turn, factsNow, { earlier = "", again = false, fallback = false } = {}) => {
    const ctrl = new AbortController();
    runs.current.set(turn.id, ctrl);
    if (factsNow.scope.read === 0) {
      patch(turn.id, { status: "done", text: "No traces match this search in this range." });
      return;
    }
    let items = [];
    let text = "";
    let note = "";
    const onEvent = (event) => {
      items = reduceChatEvent(items, event);
      const agent = items.filter((t) => t.kind === "agent");
      text = resolveTraceRefs(agent[agent.length - 1]?.text || "", factsNow.refs);
      if (turn.question) {
        const said = [...agent].reverse().find((t) => t.tools?.length && t.text)?.text || "";
        if (said && said !== note) patch(turn.id, { note: (note = said) });
      } else if (LINKED.test(text)) patch(turn.id, { text, status: "writing" });
    };
    startExploreAnalysis({ facts: factsNow, question: turn.question || null, earlier, again, fallback, scopePath, signal: ctrl.signal, onEvent })
      .then((handle) => handle.promise)
      .then((r) => {
        if (ctrl.signal.aborted) return;
        if (r.status !== "done" && r.status !== "aborted") {
          if (fallback) patch(turn.id, { status: "error" });
          else {
            patch(turn.id, { text: "", note: "", status: "thinking" });
            write(turn, factsNow, { earlier, again, fallback: true });
          }
          return;
        }
        const final = resolveTraceRefs(typeof r.result === "string" && r.result.trim() ? r.result : text, factsNow.refs);
        if (!turn.question && !LINKED.test(final)) {
          if (again) patch(turn.id, { status: "error" });
          else write(turn, factsNow, { again: true, fallback });
          return;
        }
        patch(turn.id, final ? { text: final, status: "done" } : { status: "error" });
      }, () => { if (!ctrl.signal.aborted) patch(turn.id, { status: "error" }); });
  }, [patch, scopePath]);

  // A new scope: a new answer, thinking until its facts are read.
  useEffect(() => {
    if (analyzed.current === scopeKey) return;
    analyzed.current = scopeKey;
    setTurns((all) => [...all, { id: turnId(), kind: "answer", scopeKey, text: "", status: "thinking", waiting: true }]);
  }, [scopeKey]);
  useEffect(() => {
    if (!facts) return;
    const waiting = turns.find((t) => t.waiting && t.scopeKey === scopeKey);
    if (!waiting) return;
    patch(waiting.id, { waiting: false });
    write(waiting, facts);
  }, [facts, turns, scopeKey, patch, write]);
  // The scope's traces could not be read: the answer waiting on them says so.
  useEffect(() => {
    if (!failed) return;
    setTurns((all) => all.map((t) => (t.waiting && t.scopeKey === scopeKey ? { ...t, waiting: false, status: "error", unread: true } : t)));
  }, [failed, scopeKey]);
  useEffect(() => () => { for (const ctrl of runs.current.values()) ctrl.abort(); }, []);

  // The newest analysis pins the traces it names.
  const latest = [...turns].reverse().find((t) => t.kind === "answer" && !t.question);
  const pinKey = latest ? traceRefs(latest.text).join(",") : "";
  useEffect(() => { onPins(pinKey ? pinKey.split(",") : []); }, [pinKey, onPins]);

  // A thinking answer fills the panel below the turn before it, so its field
  // is the space the answer will take; once that height applies, the turn
  // before it is scrolled to the top.
  const newest = turns[turns.length - 1];
  const scrolled = useRef(null);
  useLayoutEffect(() => {
    const el = log.current;
    if (!el || !newest || newest.kind !== "answer" || scrolled.current === newest.id) return;
    const prev = el.querySelector(`[data-turn="${newest.id}"]`)?.previousElementSibling;
    if (!heights[newest.id]) {
      const above = prev ? prev.offsetHeight + TURN_TOP : 0;
      setHeights((h) => ({ ...h, [newest.id]: Math.max(240, el.clientHeight - above - 8) }));
      return;
    }
    scrolled.current = newest.id;
    el.scrollTop = prev ? prev.offsetTop - TURN_TOP : 0;
  }, [newest, heights]);

  const filterBy = useCallback((clause, label) => {
    setMenu(null);
    setTurns((all) => [...all, { id: turnId(), kind: "you", text: label }]);
    onFilter(clause);
  }, [onFilter]);

  const renderLever = useCallback((clause, label) => (
    <InlineAction
      onAct={(e) => setMenu({ clause, label: typeof label === "string" ? label : clause, rect: e.currentTarget.getBoundingClientRect() })}
      className="cursor-pointer whitespace-nowrap border-b border-dotted border-[color:color-mix(in_srgb,var(--ink)_45%,transparent)] text-[color:var(--ink)] outline-none hover:border-solid hover:border-[color:var(--green)] focus-visible:border-solid focus-visible:border-[color:var(--green)]"
    >
      {label}
    </InlineAction>
  ), []);
  const renderLink = useCallback(({ href, children, key }) => {
    if (href?.startsWith("filter:")) return <span key={key}>{renderLever(href.slice("filter:".length), textOf(children))}</span>;
    if (href?.startsWith("trace:")) {
      const id = href.slice("trace:".length).toLowerCase();
      return (
        <InlineAction
          key={key}
          onAct={() => onOpenTrace(id)}
          onMouseEnter={() => onLightTrace(id)}
          onMouseLeave={() => onLightTrace(null)}
          onFocus={() => onLightTrace(id)}
          onBlur={() => onLightTrace(null)}
          className="cursor-pointer rounded-[3px] bg-[color-mix(in_srgb,var(--green)_11%,transparent)] px-[3px] text-[color:var(--ink)] outline-none [box-decoration-break:clone] hover:bg-[color-mix(in_srgb,var(--green)_20%,transparent)] focus-visible:bg-[color-mix(in_srgb,var(--green)_20%,transparent)]"
        >
          {children}
        </InlineAction>
      );
    }
    return null;
  }, [renderLever, onOpenTrace, onLightTrace]);

  const ask = (event) => {
    event.preventDefault();
    const q = question.trim();
    if (!q || !facts) return;
    setQuestion("");
    const earlier = latest?.text || "";
    const answer = { id: turnId(), kind: "answer", scopeKey, text: "", status: "thinking", question: q };
    setTurns((all) => [...all, { id: turnId(), kind: "you", text: q }, answer]);
    write(answer, facts, { earlier });
  };

  return (
    <div className="relative flex h-full min-h-0 flex-col overflow-hidden rounded-[10px] border border-[color:var(--line)] bg-[color:var(--panel)]" data-explore-analysis="">
      <button type="button" aria-label="Close the analysis" onClick={onClose} className="absolute right-[8px] top-[8px] z-[2] grid h-[26px] w-[26px] place-items-center rounded-[6px] bg-[color:var(--panel)] text-[color:var(--muted-soft)] hover:bg-[color-mix(in_srgb,var(--ink)_6%,transparent)] hover:text-[color:var(--ink)]">
        <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" /></svg>
      </button>
      <div ref={log} className="flex min-h-0 flex-1 flex-col overflow-y-auto px-[24px] pb-[8px] [scrollbar-width:none]">
        {turns.map((t) => (t.kind === "you" ? (
          <div key={t.id} data-turn={t.id} className="mb-[6px] mt-[14px] max-w-[80%] self-end rounded-[7px] bg-[color:var(--control-bg)] px-[11px] py-[7px] text-[13px] leading-[18px]">{t.text}</div>
        ) : (
          <div key={t.id} data-turn={t.id} className={t !== latest && !t.question ? "opacity-[0.55]" : ""}>
            <Answer
              turn={t}
              status={t.question ? noteLine(t.note) || "Thinking" : status}
              minHeight={heights[t.id] || 0}
              fieldGone={fieldGone.has(t.id)}
              onLeft={() => setFieldGone((s) => new Set(s).add(t.id))}
              renderLink={renderLink}
              renderLever={renderLever}
              onSuggest={(f) => filterBy(f.clause, f.label)}
              onRetry={() => { patch(t.id, { status: "thinking", text: "" }); if (facts) write(t, facts); }}
            />
          </div>
        )))}
      </div>
      <form onSubmit={ask} className="m-[8px_12px_12px] flex min-h-[42px] items-center gap-[8px] rounded-[7px] border border-[color:var(--line)] bg-[color:var(--chat-input-bg)] pl-[12px] pr-[6px] focus-within:border-[color:var(--green)]">
        <input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Ask about these traces" aria-label="Ask about these traces" className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[14px] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)]" />
        <button type="submit" aria-label="Ask" disabled={!question.trim() || !facts} className="grid h-[30px] w-[30px] place-items-center rounded-[6px] bg-[color-mix(in_srgb,var(--ink)_8%,transparent)] text-[color:var(--muted)] disabled:opacity-50">
          <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M8 13V3M3.5 7.5 8 3l4.5 4.5" /></svg>
        </button>
      </form>
      {menu ? <LeverMenu menu={menu} onClose={() => setMenu(null)} onFilter={filterBy} /> : null}
    </div>
  );
}

// Filter for or out of a name the analysis wrote, beside it.
function LeverMenu({ menu, onClose, onFilter }) {
  const ref = useRef(null);
  useEffect(() => {
    const off = (e) => { if (!ref.current?.contains(e.target)) onClose(); };
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("mousedown", off);
    window.addEventListener("keydown", esc);
    return () => { window.removeEventListener("mousedown", off); window.removeEventListener("keydown", esc); };
  }, [onClose]);
  const BUTTON = "h-[26px] rounded-[6px] bg-[color-mix(in_srgb,var(--ink)_6%,transparent)] px-[9px] text-[12.5px] text-[color:var(--ink)] hover:bg-[color-mix(in_srgb,var(--ink)_11%,transparent)]";
  return (
    <div ref={ref} role="menu" style={{ left: Math.min(menu.rect.left, window.innerWidth - 220), top: menu.rect.bottom + 6 }} className="fixed z-[40] flex items-center gap-[6px] rounded-[8px] border border-[color:var(--line-strong)] bg-[color:var(--panel)] p-[5px] shadow-[0_8px_22px_rgb(27_24_19_/_12%)]">
      <button type="button" role="menuitem" className={BUTTON} onClick={() => onFilter(menu.clause, `Filter for ${menu.label}`)}>Filter for</button>
      <button type="button" role="menuitem" className={BUTTON} onClick={() => onFilter(negate(menu.clause), `Filter out ${menu.label}`)}>Filter out</button>
    </div>
  );
}

// A name or trace in the analysis's text that acts when clicked. A span, not a
// button: a button lays out as one box, so a long trace title could not wrap
// with the sentence around it.
function InlineAction({ onAct, children, ...rest }) {
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={onAct}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onAct(e); } }}
      {...rest}
    >
      {children}
    </span>
  );
}

// The words of a link's label, whatever nodes it rendered as.
function textOf(node) {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textOf).join("");
  return textOf(node.props?.children);
}
